import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import BayModal from '../BayModal';
import { defaultWidgetConfig as defaultWidgets } from '../utilities/constants/launchpadConstants';
import { launchpadCustomizationDefaultState } from '../utilities/constants/launchpadConstants';

import LaunchpadContent from './components/LaunchpadContent';
import AdsAndLeadsWidget from './components/Widgets/AdsAndLeads';
import BannerWidget from './components/Widgets/Banner';
import CrmWidget from './components/Widgets/Crm';
import FinancialWidget from './components/Widgets/Financial';
import HeaderWidget from './components/Widgets/Header';
import HeaderActionsWidget from './components/Widgets/HeaderActions';
import LandingPagesWidget from './components/Widgets/LandingPages';
import ListingsWidget from './components/Widgets/Listings';
import ListingsSmcWidget from './components/Widgets/ListingsSmc';
import MarketingWidget from './components/Widgets/Marketing';
import OtherLinksWidget from './components/Widgets/OtherLinks';
import WebsiteWidget from './components/Widgets/Websites';

import './BayLaunchpad.scss';

declare const $: any;

interface BayLaunchpadProps {
	t: (_key: string) => string;
	user: any;
	bannerType?: string;
	handleClose: () => void;
	showLaunchpadModal: boolean;
	setShowLaunchpadModal: (_show: boolean) => void;
	readLaunchpadApi?: any;
	updateLaunchpadApi?: any;
	fetchWebsiteDetailsApi?: any;
	selectLaunchpadSettingsData: any;
	selectUserProductAccess: any;
	selectLaunchpadStatus: any;
	setLaunchpadSettingsData?: any;
	setUserProductAccess?: any;
	setWebsiteDetails?: any;
	uploadHeaderImageApi?: any;
	uploadBannerImageApi?: any;
	selectWebsiteDetails?: any;
	setSitesBayWebsiteDetails?: any;
	selectSitesBayWebsiteDetails?: any;
	appOrigin?: string;
	platformType?: string;
	sharedAppOrigin?: string;
}

const BayLaunchpad: React.FC<BayLaunchpadProps> = React.memo((props) => {
	const {
		t,
		user,
		bannerType,
		handleClose,
		showLaunchpadModal,
		setShowLaunchpadModal,
		readLaunchpadApi,
		updateLaunchpadApi,
		fetchWebsiteDetailsApi,
		selectLaunchpadSettingsData,
		selectUserProductAccess,
		selectLaunchpadStatus,
		setLaunchpadSettingsData,
		setUserProductAccess,
		setWebsiteDetails,
		uploadHeaderImageApi,
		uploadBannerImageApi,
		selectWebsiteDetails,
		setSitesBayWebsiteDetails,
		selectSitesBayWebsiteDetails,
	} = props;

	const dispatch = useDispatch();

	const launchpadSettingsData = useSelector(selectLaunchpadSettingsData) as any;
	const userProductAccess = useSelector(selectUserProductAccess) as any;
	const launchpadStatus = useSelector(selectLaunchpadStatus) as any;

	const [widgets, setWidgets] = useState<any[]>([]);
	const [snackbar, setSnackbar] = useState({
		show: false,
		description: '',
	});
	const [currentBreakpoint, setCurrentBreakpoint] = useState('lg');
	const [headerImageUrl, setHeaderImageUrl] = useState('');
	const [isInPreviewMode, setIsInPreviewMode] = useState(false);
	const [widgetCustomizationData, setWidgetCustomizationData] = useState<any>({});
	const [widgetFinishStatus, setWidgetFinishStatus] = useState<any>({});
	const [editModalInputFields, setEditModalInputFields] = useState<any>({});
	const [bannerImageUrl, setBannerImageUrl] = useState('');
	const [width, setWidth] = useState(0);

	const [hasCustomizationPermission, setHasCustomizationPermission] =
		useState(false);

	useEffect(() => {
		if (!showLaunchpadModal) {
			setSnackbar({
				show: false,
				description: '',
			});
		}
	}, [showLaunchpadModal]);

	useEffect(() => {
		const fetchLaunchpadData = async () => {
			if (!user || !readLaunchpadApi) {
				return;
			}

			let effectiveUserId;
			if (
				user?.ActingAsBAY_PARENT_ACCOUNT_ID &&
				user.ActingAsBAY_PARENT_ACCOUNT_ID !== ''
			) {
				effectiveUserId = user.ActingAsBAY_PARENT_ACCOUNT_ID;
			} else if (user?.parent_account_id && user.parent_account_id !== '') {
				effectiveUserId = user.parent_account_id;
			} else {
				effectiveUserId =
					user?.platform === 'social' ? user?.PUBLICID : user?._companyid;
			}

			if (!effectiveUserId) {
				console.warn(
					'Launchpad: Could not determine effectiveUserId for read.',
				);
				return;
			}

			try {
				if (fetchWebsiteDetailsApi) {
					await dispatch(
						fetchWebsiteDetailsApi({
							userId:
								user?.agent_id !== ''
									? user?.agent_id
									: user?.platform === 'social'
										? user?.PUBLICID
										: user?.ActingAsPUBLICID,
						}),
					).unwrap();
				}

				await dispatch(
					readLaunchpadApi({
						component: 'launchpadSettings',
						realm: 'BAY',
						userId: effectiveUserId,
						isCompanyContext: true,
					}),
				).unwrap();
			} catch (error) {
				console.error('Error fetching launchpad data:', error);
			}
		};

		fetchLaunchpadData();
	}, []);

	useEffect(() => {
		if (!showLaunchpadModal) return;

		const hasSocialAccess = userProductAccess?.social;

		const getProcessedWidgets = () => {
			let updatedMarketingWidgetWidth = 3;
			const updatedMarketingWidgetX = 0;
			let updatedBannerWidgetWidth = 1;
			let updatedBannerWidgetX = 3;
			let flag = false;

			if (width > 900) {
				const ImageWidth = 240;
				const SingleColumnWidth = width / 5;
				updatedBannerWidgetWidth = ImageWidth / SingleColumnWidth;

				if (updatedBannerWidgetWidth < 1) {
					flag = true;
					updatedMarketingWidgetWidth = 3 + (0.975 - updatedBannerWidgetWidth);
					updatedBannerWidgetX = 3 + (updatedMarketingWidgetWidth - 3);
				}
			}

			const widgetAdjustments: any = {
				'widget-marketing': (widget: any) => ({
					...widget,
					dims: { h: 4.2, w: flag ? updatedMarketingWidgetWidth : 3 },
					coords: { x: flag ? updatedMarketingWidgetX : 0, y: 1 },
				}),
				'widget-banner': (widget: any) => ({
					...widget,
					dims: { h: 4.2, w: flag ? updatedBannerWidgetWidth : 1 },
					coords: { x: flag ? updatedBannerWidgetX : 3, y: 1 },
				}),
			};

			const shouldIncludeWidget = (widget: any) => {
				if (widget.id === 'widget-financial' && !userProductAccess?.financial) {
					return false;
				}

				if (userProductAccess?.client) {
					return [
						'widget-crm',
						'widget-listings',
						'widget-websites',
						'widget-header',
						'widget-header-action',
						'widget-marketing',
						'widget-banner',
						'widget-otherLinks',
						'widget-financial',
					].includes(widget.id);
				} else if (userProductAccess?.social) {
					return [
						'widget-adsAndLeads',
						'widget-listingsSmc',
						'widget-landingPages',
						'widget-header',
						'widget-header-action',
						'widget-marketing',
						'widget-banner',
						'widget-otherLinks',
						'widget-financial',
					].includes(widget.id);
				}

				return [
					'widget-header',
					'widget-header-action',
					'widget-marketing',
					'widget-banner',
					'widget-otherLinks',
					'widget-financial',
				].includes(widget.id);
			};

			const applyWidgetAdjustments = (widget: any) => {
				let adjustedWidget = { ...widget };

				if (!userProductAccess?.financial && !hasSocialAccess) {
					if (
						userProductAccess?.client &&
						!userProductAccess?.financial &&
						!userProductAccess?.sites &&
						!userProductAccess?.social
					) {
						const ClientAdjustments: any = {
							'widget-crm': { w: 1.33, x: 0 },
							'widget-listings': { w: 1.33, x: 1.33 },
							'widget-websites': { w: 1.33, x: 2.66 },
						};

						if (ClientAdjustments[widget.id]) {
							const adjustment = ClientAdjustments[widget.id];
							adjustedWidget = {
								...adjustedWidget,
								dims: { ...adjustedWidget.dims, w: adjustment.w },
								coords: { ...adjustedWidget.coords, x: adjustment.x },
							};
						}
					} else {
						const dimensionAdjustments: any = {
							'widget-crm': { w: 1.33 },
							'widget-listings': { w: 1.33 },
							'widget-websites': { w: 1.33 },
						};

						if (dimensionAdjustments[widget.id]) {
							const adjustment = dimensionAdjustments[widget.id];
							adjustedWidget = {
								...adjustedWidget,
								dims: { ...adjustedWidget.dims, w: adjustment.w },
							};
						}
					}
				}

				if (userProductAccess?.client && !userProductAccess?.financial) {
					const clientWidgetAdjustments: any = {
						'widget-crm': { w: 1.33, x: 0, minW: 1, maxW: 2 },
						'widget-listings': { w: 1.33, x: 1.33, minW: 1, maxW: 2 },
						'widget-websites': { w: 1.33, x: 2.66, minW: 1, maxW: 2 },
					};

					if (clientWidgetAdjustments[widget.id]) {
						const adjustment = clientWidgetAdjustments[widget.id];
						adjustedWidget = {
							...adjustedWidget,
							dims: { ...adjustedWidget.dims, w: adjustment.w },
							coords: { ...adjustedWidget.coords, x: adjustment.x },
							minW: adjustment.minW,
							maxW: adjustment.maxW,
						};
					}
				}

				if (userProductAccess?.social && !userProductAccess?.financial) {
					const socialWidgetAdjustments: any = {
						'widget-adsAndLeads': { w: 1.33, x: 0 },
						'widget-listingsSmc': { w: 1.33, x: 1.33 },
						'widget-landingPages': { w: 1.33, x: 2.66 },
					};

					if (socialWidgetAdjustments[widget.id]) {
						const adjustment = socialWidgetAdjustments[widget.id];
						adjustedWidget = {
							...adjustedWidget,
							dims: { ...adjustedWidget.dims, w: adjustment.w },
							coords: { ...adjustedWidget.coords, x: adjustment.x },
						};
					}
				}

				if (userProductAccess?.financial && hasSocialAccess) {
					const socialWidgets: any = {
						'widget-adsAndLeads': { x: 0, y: 5 },
						'widget-listingsSmc': { x: 1, y: 5 },
						'widget-landingPages': { x: 2, y: 5 },
					};

					if (socialWidgets[widget.id]) {
						adjustedWidget = {
							...adjustedWidget,
							dims: { h: 3.5, w: 1 },
							coords: socialWidgets[widget.id],
						};
					}
				}

				const standardAdjustment = widgetAdjustments[widget.id];
				if (standardAdjustment) {
					adjustedWidget = standardAdjustment(adjustedWidget);
				}

				return adjustedWidget;
			};

			return defaultWidgets
				.filter(shouldIncludeWidget)
				.map(applyWidgetAdjustments);
		};

		const applyBreakpointAdjustments = (widgets: any[]) => {
			const getInlineCustomizationPermission = () => {
				if (user.platform === 'social') {
					return user?.isEnterprise;
				} else if (user.platform === 'client') {
					return user?.ActingAsACCESSLEVEL === 'A';
				}
				return false;
			};

			const inlineHasCustomizationPermission =
				getInlineCustomizationPermission();

			return widgets.map((widget) => {
				const { id } = widget;

				if (currentBreakpoint === 'xxs' || currentBreakpoint === 'xxxs') {
					if (id === 'widget-header')
						return { ...widget, dims: { h: 1.4, w: 1 } };
					if (id === 'widget-marketing')
						return {
							...widget,
							dims: { h: currentBreakpoint === 'xxs' ? 7.5 : 8.0, w: 1 },
						};
					if (id === 'widget-header-action') {
						return {
							...widget,
							dims: { h: inlineHasCustomizationPermission ? 0.7 : 0, w: 2 },
						};
					}
					if (id === 'widget-otherLinks') {
						return {
							...widget,
							dims: { h: 3.5, w: 1 },
							coords: { x: 0, y: currentBreakpoint === 'xxs' ? 20 : 21 },
						};
					}
				}

				if (currentBreakpoint === 'xs') {
					if (id === 'widget-header')
						return { ...widget, dims: { h: 1.1, w: 1 } };
					if (id === 'widget-header-action') {
						return {
							...widget,
							dims: { h: inlineHasCustomizationPermission ? 0.7 : 0, w: 2 },
						};
					}
					if (id === 'widget-marketing')
						return { ...widget, dims: { h: 7.5, w: 1 } };
					if (id === 'widget-otherLinks') {
						return {
							...widget,
							dims: { h: 3.5, w: 1 },
							coords: { x: 0, y: 18 },
						};
					}
				}

				if (currentBreakpoint === 'sm') {
					if (id === 'widget-header-action') {
						return {
							...widget,
							dims: { h: inlineHasCustomizationPermission ? 0.7 : 0, w: 2 },
						};
					}
					if (id === 'widget-otherLinks') {
						return {
							...widget,
							dims: { h: 3.5, w: 1 },
							coords: { x: 0, y: 18 },
						};
					}
				}

				if (currentBreakpoint === 'md') {
					if (id === 'widget-header-action') {
						return {
							...widget,
							dims: { h: inlineHasCustomizationPermission ? 0.7 : 0, w: 2 },
						};
					}
					if (id === 'widget-crm' || id === 'widget-adsAndLeads')
						return {
							...widget,
							dims: { h: 3.5, w: 1 },
							coords: { x: 0, y: 5 },
						};
					if (id === 'widget-listings' || id === 'widget-listingsSmc') {
						return {
							...widget,
							dims: { h: 3.5, w: 1 },
							coords: { x: 0, y: 8.5 },
						};
					}
					if (id === 'widget-websites' || id === 'widget-landingPages') {
						return {
							...widget,
							dims: { h: 3.5, w: 1 },
							coords: { x: 0, y: 12 },
						};
					}
					if (id === 'widget-financial') {
						return {
							...widget,
							dims: { h: 3.5, w: 1 },
							coords: { x: 0, y: 15.5 },
						};
					}
				}

				if (currentBreakpoint === 'lg' || currentBreakpoint === 'xl') {
					if (id === 'widget-otherLinks')
						return { ...widget, dims: { h: 11.2, w: 1 } };

					if (userProductAccess?.client) {
						if (userProductAccess?.financial) {
							if (width && width >= 1004 && width <= 1220) {
								if (id === 'widget-crm')
									return {
										...widget,
										dims: { h: 3.5, w: 2 },
										coords: { x: 0, y: 5 },
									};
								if (id === 'widget-listings') {
									return {
										...widget,
										dims: { h: 3.5, w: 2 },
										coords: { x: 2, y: 5 },
									};
								}
								if (id === 'widget-websites') {
									return {
										...widget,
										dims: { h: 3.5, w: 2 },
										coords: { x: 0, y: 8.5 },
									};
								}
								if (id === 'widget-financial') {
									return {
										...widget,
										dims: { h: 3.5, w: 2 },
										coords: { x: 2, y: 8.5 },
									};
								}
							}
						} else {
							if (width && width > 1342) {
								// No changes needed for large width
							} else if (width && width >= 1104 && width <= 1342) {
								if (id === 'widget-crm')
									return {
										...widget,
										dims: { h: 3.5, w: 2 },
										coords: { x: 0, y: 5 },
										minW: 1,
										maxW: 4,
									};
								if (id === 'widget-listings') {
									return {
										...widget,
										dims: { h: 3.5, w: 2 },
										coords: { x: 2, y: 5 },
										minW: 1,
										maxW: 4,
									};
								}
								if (id === 'widget-websites') {
									return {
										...widget,
										dims: { h: 3.5, w: 4 },
										coords: { x: 0, y: 8.5 },
										minW: 1,
										maxW: 4,
									};
								}
							}
						}
					} else if (userProductAccess?.social) {
						if (userProductAccess?.financial) {
							if (width && width >= 1004 && width <= 1220) {
								if (id === 'widget-adsAndLeads')
									return {
										...widget,
										dims: { h: 3.5, w: 2 },
										coords: { x: 0, y: 5 },
									};
								if (id === 'widget-listingsSmc') {
									return {
										...widget,
										dims: { h: 3.5, w: 2 },
										coords: { x: 2, y: 5 },
									};
								}
								if (id === 'widget-landingPages') {
									return {
										...widget,
										dims: { h: 3.5, w: 2 },
										coords: { x: 0, y: 8.5 },
									};
								}
								if (id === 'widget-financial') {
									return {
										...widget,
										dims: { h: 3.5, w: 2 },
										coords: { x: 2, y: 8.5 },
									};
								}
							}
						} else {
							if (width && width > 1342) {
								// No changes needed for large width
							} else if (width && width >= 1004 && width <= 1342) {
								if (id === 'widget-adsAndLeads')
									return {
										...widget,
										dims: { h: 3.5, w: 2 },
										coords: { x: 0, y: 5 },
										minW: 1,
										maxW: 4,
									};
								if (id === 'widget-listingsSmc') {
									return {
										...widget,
										dims: { h: 3.5, w: 2 },
										coords: { x: 2, y: 5 },
										minW: 1,
										maxW: 4,
									};
								}
								if (id === 'widget-landingPages') {
									return {
										...widget,
										dims: { h: 3.5, w: 4 },
										coords: { x: 0, y: 8.5 },
										minW: 1,
										maxW: 4,
									};
								}
							}
						}
					}
				}

				return widget;
			});
		};

		if (!userProductAccess || isEmpty(userProductAccess)) {
			const filteredWidgets = defaultWidgets.filter(
				(widget) =>
					widget.id !== 'widget-adsAndLeads' &&
					widget.id !== 'widget-landingPages' &&
					widget.id !== 'widget-listingsSmc',
			);

			if (filteredWidgets.length !== widgets.length) {
				setWidgets(filteredWidgets);
			}
			return;
		}

		const processedWidgets = getProcessedWidgets();
		const finalWidgets = applyBreakpointAdjustments(processedWidgets);

		const getCustomizationPermissions = () => {
			if (user.platform === 'social') {
				return user?.isEnterprise;
			} else if (user.platform === 'client') {
				return user?.ActingAsACCESSLEVEL === 'A';
			}
			return false;
		};

		setHasCustomizationPermission(getCustomizationPermissions());

		setWidgets(finalWidgets);
	}, [showLaunchpadModal, userProductAccess, width, currentBreakpoint]);

	const handleLinkClick = (e: React.MouseEvent, linkPath: string, useSSO = false) => {
		if (linkPath.startsWith('#')) {
			e.preventDefault();

			if (linkPath.includes('myCreator')) {
				setShowLaunchpadModal(false);

				if (user.platform === 'client') {
					const ssoForm = document.getElementById('smcredirect') as HTMLFormElement;
					if (ssoForm) {
						let nextUrlInput = document.getElementById('iq_next_url') as HTMLInputElement;
						if (!nextUrlInput) {
							nextUrlInput = document.createElement('input');
							nextUrlInput.type = 'hidden';
							nextUrlInput.name = 'next_url';
							nextUrlInput.id = 'iq_next_url';
							ssoForm.appendChild(nextUrlInput);
						}

						nextUrlInput.value = '/#/myCreator';

						ssoForm.submit();
						($ as any).iqUIProcessing('show');
						return;
					}
				}

				window.location.hash = '#/myCreator';
				return;
			}

			setShowLaunchpadModal(false);

			if (linkPath !== '#launchpad') {
				window.location.hash = linkPath;
			}
			return;
		}

		if (user.platform === 'client' && useSSO) {
			e.preventDefault();

			const ssoForm = document.getElementById('smcredirect') as HTMLFormElement;
			if (ssoForm) {
				let nextUrlInput = document.getElementById('iq_next_url') as HTMLInputElement;
				if (!nextUrlInput) {
					nextUrlInput = document.createElement('input');
					nextUrlInput.type = 'hidden';
					nextUrlInput.name = 'next_url';
					nextUrlInput.id = 'iq_next_url';
					ssoForm.appendChild(nextUrlInput);
				}

				nextUrlInput.value = linkPath;

				ssoForm.submit();
				setShowLaunchpadModal(false);
				($ as any).iqUIProcessing('show');
				return;
			}
		}

		if (user.platform === 'social' && useSSO) {
			e.preventDefault();

			const socialSsoForm = document.getElementById('clientbayredirect') as HTMLFormElement;
			if (socialSsoForm) {
				let nextUrlInput = document.getElementById('sso_next_url') as HTMLInputElement;
				if (!nextUrlInput) {
					nextUrlInput = document.createElement('input');
					nextUrlInput.type = 'hidden';
					nextUrlInput.name = 'redirect';
					nextUrlInput.id = 'sso_next_url';
					socialSsoForm.appendChild(nextUrlInput);
				}

				nextUrlInput.value = linkPath;

				socialSsoForm.submit();
				($ as any).iqUIProcessing('show');
				setShowLaunchpadModal(false);
				return;
			}
		}

		const currentPagePath = window.location.pathname + window.location.search;
		const linkBasePath = linkPath.split('#')[0];

		e.preventDefault();

		if (
			!linkPath.startsWith('http') &&
			!linkPath.startsWith('https') &&
			!linkPath.startsWith('/?') &&
			!linkPath.startsWith('/')
		) {
			linkPath = `https://${linkPath}`;
		}

		if (e.type === 'click') {
			window.open(
				linkPath,
				!linkPath.startsWith('/?') && !linkPath.startsWith('/')
					? '_blank'
					: '_self',
				'noopener,noreferrer',
			);
		}

		if (currentPagePath === linkBasePath) {
			setShowLaunchpadModal(false);
		}
	};

	const getWidgetByIdFn = ({
		widgetId,
		editMode,
		isPreviewWidget = false,
		iseditable = false,
		handleEditFunction = () => {},
		handleClose = () => {},
		setShowModal = () => {},
		setShowConformationModal = () => {},
		setIsRestoreDefault = () => {},
		setShowNoFeatureModal = () => {},
		currentBreakpoint = '',
		handleSaveLaunchpadConfig = () => {},
	}: any) => {
		const widgetUpdatedData = ({
			widgetId,
			key,
			shouldUseWidgetCustomizationData,
		}: any) => {
			return shouldUseWidgetCustomizationData
				? widgetCustomizationData?.widgets?.[widgetId]?.[key]
				: launchpadSettingsData?.widgets?.[widgetId]?.[key] ??
						(launchpadCustomizationDefaultState as any)?.[widgetId]?.[key];
		};

		const getStaticWidgetData = (widgetId: string) => {
			const settingsData = launchpadSettingsData?.widgets?.[widgetId];
			const defaultData = (launchpadCustomizationDefaultState as any)[widgetId];

			const hasCompleteData = (data: any) => {
				if (!data) return false;

				if (
					![
						'widget-banner',
						'widget-header',
						'widget-header-action',
						'widget-otherLinks',
					].includes(widgetId)
				) {
					return (
						data.links &&
						data.links.length > 0 &&
						data.headerTitle &&
						data.headerIcon
					);
				}

				if (widgetId === 'widget-banner') {
					return data.bannerImage !== undefined;
				}

				if (widgetId === 'widget-header') {
					return data.headerTitle !== undefined;
				}

				if (widgetId === 'widget-otherLinks') {
					return data.sections !== undefined;
				}

				return true;
			};

			if (!hasCompleteData(settingsData)) {
				return {
					...defaultData,
					...settingsData,
				};
			}

			return settingsData;
		};

		const currentWidget = widgets.find((widget) => widget.id === widgetId);

		const checkAndAdjustLinkPath = (props: any) => {
			const { linkPath, activeSection } = props;

			const widgetPlatforms: any = {
				crm: 'client',
				financial: 'client',
				websites: 'client',
				email: 'client',
				adsAndLeads: 'social',
				landingPages: 'social',
				listingsSmc: 'social',
				marketing: 'social',
				socialMedia: 'social',
				print: 'social',
				listings: 'client',
			};

			const widgetPlatform = widgetPlatforms[activeSection];
			const needsSSO = widgetPlatform && widgetPlatform !== user.platform;

			return {
				linkPath,
				useSSO: needsSSO,
			};
		};

		const bannerConfig = {
			bannerImage: widgetUpdatedData({
				widgetId,
				key: 'bannerImage',
				shouldUseWidgetCustomizationData: widgetFinishStatus?.[widgetId],
			}),
			bannerHoverText: widgetUpdatedData({
				widgetId,
				key: 'bannerHoverText',
				shouldUseWidgetCustomizationData: widgetFinishStatus?.[widgetId],
			}),
			bannerHoverUrl: widgetUpdatedData({
				widgetId,
				key: 'bannerHoverUrl',
				shouldUseWidgetCustomizationData: widgetFinishStatus?.[widgetId],
			}),
		};

		switch (widgetId) {
		case 'widget-header':
			return (
				<HeaderWidget
					currentBreakpoint={currentBreakpoint}
					editModalInputFields={editModalInputFields}
					editMode={editMode}
					handleClose={handleClose}
					headerDescription={widgetUpdatedData({
						widgetId,
						key: 'headerTitleDescription',
						shouldUseWidgetCustomizationData: isInPreviewMode,
					})}
					headerImageUrl={widgetUpdatedData({
						widgetId,
						key: 'headerImage',
						shouldUseWidgetCustomizationData: isInPreviewMode,
					})}
					headerTitle={widgetUpdatedData({
						widgetId,
						key: 'headerTitle',
						shouldUseWidgetCustomizationData: isInPreviewMode,
					})}
					isHeaderWidget
					isInPreviewMode={isInPreviewMode}
					isLoading={launchpadStatus === 'loading'}
					launchpadSettingsData={launchpadSettingsData}
					selectLaunchpadSettingsData={selectLaunchpadSettingsData}
					setEditModalInputFields={setEditModalInputFields}
					setHeaderImageUrl={setHeaderImageUrl}
					setIsInPreviewMode={setIsInPreviewMode}
					setSnackbar={setSnackbar}
					setWidgetCustomizationData={setWidgetCustomizationData}
					setWidgetFinishStatus={setWidgetFinishStatus}
					setWidgets={setWidgets}
					t={t}
					widgetCustomizationData={
						widgetCustomizationData?.widgets?.[widgetId]
					}
					widgetDetails={currentWidget}
					widgetFinishStatus={widgetFinishStatus}
				/>
			);
		case 'widget-header-action':
			return (
				<HeaderActionsWidget
					{...{
						currentBreakpoint,
						editMode,
						handleClose,
						handleEditFunction,
						handleSaveLaunchpadConfig,
						hasCustomizationPermission,
						iseditable,
						isLoading: launchpadStatus === 'loading',
						selectLaunchpadSettingsData,
						setIsInPreviewMode,
						setIsRestoreDefault,
						setShowConformationModal,
						setShowModal,
						setShowNoFeatureModal,
						setSnackbar,
						t,
					} as any}
				/>
			);
		case 'widget-marketing':
			return (
				<MarketingWidget
					{...{
						checkAndAdjustLinkPath,
						currentBreakpoint,
						defaultWidgetStyles: getStaticWidgetData(widgetId),
						editModalInputFields,
						editMode,
						editWidgetHeight: 900,
						handleLinkClick,
						isInPreviewMode: widgetFinishStatus?.[widgetId],
						isLoading: launchpadStatus === 'loading',
						isPreviewWidget,
						selectLaunchpadSettingsData,
						selectUserProductAccess,
						setEditModalInputFields,
						setSnackbar,
						setWidgetCustomizationData,
						setWidgetFinishStatus,
						setWidgets,
						t,
						user,
						widgetCustomizationData:
								widgetCustomizationData?.widgets?.[widgetId],
						widgetDetails: currentWidget,
						widgetDrawerThemeSettings:
								widgetCustomizationData?.drawerThemeSettings,
						widgetFinishStatus,
						widgets,
					} as any}
				/>
			);
		case 'widget-banner':
			return (
				<BannerWidget
					{...{
						bannerConfig,
						bannerImageUrl,
						defaultWidgetStyles: getStaticWidgetData(widgetId),
						editModalInputFields,
						editMode,
						handleLinkClick,
						isInPreviewMode: widgetFinishStatus?.[widgetId],
						isLoading: launchpadStatus === 'loading',
						launchpadSettingsData,
						selectLaunchpadSettingsData,
						setBannerImageUrl,
						setEditModalInputFields,
						setSnackbar,
						setWidgetCustomizationData,
						setWidgetFinishStatus,
						setWidgets,
						t,
						widgetCustomizationData:
								widgetCustomizationData?.widgets?.[widgetId],
						widgetDetails: currentWidget,
						widgetFinishStatus,
						widgets,
					} as any}
				/>
			);
		case 'widget-crm':
			return (
				<CrmWidget
					{...{
						checkAndAdjustLinkPath,
						defaultWidgetStyles: getStaticWidgetData(widgetId),
						editModalInputFields,
						editMode,
						handleLinkClick,
						isInPreviewMode: widgetFinishStatus?.[widgetId],
						isLoading: launchpadStatus === 'loading',
						isPreviewWidget,
						selectLaunchpadSettingsData,
						setEditModalInputFields,
						setShowLaunchpadModal,
						setSnackbar,
						setWidgetCustomizationData,
						setWidgetFinishStatus,
						setWidgets,
						t,
						widgetCustomizationData:
								widgetCustomizationData?.widgets?.[widgetId],
						widgetDetails: currentWidget,
						widgetDrawerThemeSettings:
								widgetCustomizationData?.drawerThemeSettings,
						widgetFinishStatus,
						widgets,
					} as any}
				/>
			);
		case 'widget-listings':
			return (
				<ListingsWidget
					{...{
						checkAndAdjustLinkPath,
						defaultWidgetStyles: getStaticWidgetData(widgetId),
						editModalInputFields,
						editMode,
						handleLinkClick,
						isInPreviewMode: widgetFinishStatus?.[widgetId],
						isLoading: launchpadStatus === 'loading',
						isPreviewWidget,
						selectLaunchpadSettingsData,
						setEditModalInputFields,
						setSnackbar,
						setWidgetCustomizationData,
						setWidgetFinishStatus,
						setWidgets,
						t,
						widgetCustomizationData:
								widgetCustomizationData?.widgets?.[widgetId],
						widgetDetails: currentWidget,
						widgetDrawerThemeSettings:
								widgetCustomizationData?.drawerThemeSettings,
						widgetFinishStatus,
						widgets,
					} as any}
				/>
			);
		case 'widget-websites':
			return (
				<WebsiteWidget
					{...{
						checkAndAdjustLinkPath,
						currentBreakpoint,
						defaultWidgetStyles: getStaticWidgetData(widgetId),
						editModalInputFields,
						editMode,
						handleLinkClick,
						isInPreviewMode: widgetFinishStatus?.[widgetId],
						isLoading: launchpadStatus === 'loading',
						isPreviewWidget,
						selectLaunchpadSettingsData,
						selectSitesBayWebsiteDetails,
						selectWebsiteDetails,
						setEditModalInputFields,
						setSnackbar,
						setWidgetCustomizationData,
						setWidgetFinishStatus,
						setWidgets,
						t,
						widgetCustomizationData:
								widgetCustomizationData?.widgets?.[widgetId],
						widgetDetails: currentWidget,
						widgetDrawerThemeSettings:
								widgetCustomizationData?.drawerThemeSettings,
						widgetFinishStatus,
						widgets,
					} as any}
				/>
			);
		case 'widget-financial':
			return (
				<FinancialWidget
					{...{
						checkAndAdjustLinkPath,
						defaultWidgetStyles: getStaticWidgetData(widgetId),
						editModalInputFields,
						editMode,
						handleLinkClick,
						isInPreviewMode: widgetFinishStatus?.[widgetId],
						isLoading: launchpadStatus === 'loading',
						isPreviewWidget,
						selectLaunchpadSettingsData,
						setEditModalInputFields,
						setSnackbar,
						setWidgetCustomizationData,
						setWidgetFinishStatus,
						setWidgets,
						t,
						widgetCustomizationData:
								widgetCustomizationData?.widgets?.[widgetId],
						widgetDetails: currentWidget,
						widgetDrawerThemeSettings:
								widgetCustomizationData?.drawerThemeSettings,
						widgetFinishStatus,
						widgets,
					} as any}
				/>
			);
		case 'widget-otherLinks':
			return (
				<OtherLinksWidget
					{...{
						defaultWidgetStyles: getStaticWidgetData(widgetId),
						editModalInputFields,
						editMode,
						handleLinkClick,
						isInPreviewMode: widgetFinishStatus?.[widgetId],
						isLoading: launchpadStatus === 'loading',
						isPreviewWidget,
						selectLaunchpadSettingsData,
						setEditModalInputFields,
						setSnackbar,
						setWidgetCustomizationData,
						setWidgetFinishStatus,
						setWidgets,
						t,
						widgetCustomizationData:
								widgetCustomizationData?.widgets?.[widgetId],
						widgetDetails: currentWidget,
						widgetFinishStatus,
						widgets,
					} as any}
				/>
			);
		case 'widget-adsAndLeads':
			return (
				<AdsAndLeadsWidget
					{...{
						checkAndAdjustLinkPath,
						defaultWidgetStyles: getStaticWidgetData(widgetId),
						editModalInputFields,
						editMode,
						handleLinkClick,
						isInPreviewMode: widgetFinishStatus?.[widgetId],
						isLoading: launchpadStatus === 'loading',
						isPreviewWidget,
						selectLaunchpadSettingsData,
						setEditModalInputFields,
						setShowLaunchpadModal,
						setSnackbar,
						setWidgetCustomizationData,
						setWidgetFinishStatus,
						setWidgets,
						t,
						widgetCustomizationData:
								widgetCustomizationData?.widgets?.[widgetId],
						widgetDetails: currentWidget,
						widgetDrawerThemeSettings:
								widgetCustomizationData?.drawerThemeSettings,
						widgetFinishStatus,
						widgets,
					} as any}
				/>
			);
		case 'widget-listingsSmc':
			return (
				<ListingsSmcWidget
					{...{
						checkAndAdjustLinkPath,
						defaultWidgetStyles: getStaticWidgetData(widgetId),
						editModalInputFields,
						editMode,
						handleLinkClick,
						isInPreviewMode: widgetFinishStatus?.[widgetId],
						isLoading: launchpadStatus === 'loading',
						isPreviewWidget,
						selectLaunchpadSettingsData,
						setEditModalInputFields,
						setSnackbar,
						setWidgetCustomizationData,
						setWidgetFinishStatus,
						setWidgets,
						t,
						widgetCustomizationData:
								widgetCustomizationData?.widgets?.[widgetId],
						widgetDetails: currentWidget,
						widgetDrawerThemeSettings:
								widgetCustomizationData?.drawerThemeSettings,
						widgetFinishStatus,
						widgets,
					} as any}
				/>
			);
		case 'widget-landingPages':
			return (
				<LandingPagesWidget
					{...{
						checkAndAdjustLinkPath,
						defaultWidgetStyles: getStaticWidgetData(widgetId),
						editModalInputFields,
						editMode,
						handleLinkClick,
						isInPreviewMode: widgetFinishStatus?.[widgetId],
						isLoading: launchpadStatus === 'loading',
						isPreviewWidget,
						selectLaunchpadSettingsData,
						setEditModalInputFields,
						setShowLaunchpadModal,
						setSnackbar,
						setWidgetCustomizationData,
						setWidgetFinishStatus,
						setWidgets,
						t,
						widgetCustomizationData:
								widgetCustomizationData?.widgets?.[widgetId],
						widgetDetails: currentWidget,
						widgetDrawerThemeSettings:
								widgetCustomizationData?.drawerThemeSettings,
						widgetFinishStatus,
						widgets,
					} as any}
				/>
			);
		default:
			return null;
		}
	};

	const onWidthChange = (width: number) => {
		setWidth(width);
	};

	const getWidgetFields = (widgetId: string) => {
		return (
			launchpadSettingsData?.widgets?.[widgetId] ||
			(launchpadCustomizationDefaultState as any)[widgetId]
		);
	};

	return (
		<BayModal
			backdrop="static"
			centered
			className="bay-launchpad-modal"
			closeButton={false}
			height="100%"
			hideFooter
			hideHeader
			id="launchpad_modal"
			show={showLaunchpadModal}
			size="xl"
			onHideFunc={handleClose}
		>
			<LaunchpadContent {...{
				adsAndLeadsWidgetFields: getWidgetFields('widget-adsAndLeads'),
				bannerImageUrl,
				bannerType,
				bannerWidgetFields: getWidgetFields('widget-banner'),
				crmWidgetFields: getWidgetFields('widget-crm'),
				currentBreakpoint,
				defaultWidgets,
				dispatchCurrentData: setWidgets,
				editModalInputFields,
				existingLayout: defaultWidgets,
				financialWidgetFields: getWidgetFields('widget-financial'),
				getWidgetByIdFn,
				handleClose,
				headerImageUrl,
				isInPreviewMode,
				landingPagesWidgetFields: getWidgetFields('widget-landingPages'),
				launchpadSettingsData,
				listingsSmcWidgetFields: getWidgetFields('widget-listingsSmc'),
				listingsWidgetFields: getWidgetFields('widget-listings'),
				marketingWidgetFields: getWidgetFields('widget-marketing'),
				otherLinksWidgetFields: getWidgetFields('widget-otherLinks'),
				readLaunchpadApi,
				selectLaunchpadSettingsData,
				selectLaunchpadStatus,
				selectSitesBayWebsiteDetails,
				selectUserProductAccess,
				selectWebsiteDetails,
				setBannerImageUrl,
				setCurrentBreakpoint,
				setEditModalInputFields,
				setHeaderImageUrl,
				setIsInPreviewMode,
				setLaunchpadSettingsData,
				setSitesBayWebsiteDetails,
				setSnackbar: setSnackbar as any,
				setUserProductAccess,
				setWebsiteDetails,
				setWidgetCustomizationData,
				setWidgetFinishStatus,
				snackbar,
				t,
				updateLaunchpadApi,
				uploadBannerImageApi,
				uploadHeaderImageApi,
				user,
				websitesWidgetFields: getWidgetFields('widget-websites'),
				widgetCustomizationData,
				widgetFinishStatus,
				widgets,
				onWidthChange,
			} as any} />
		</BayModal>
	);
});

BayLaunchpad.displayName = 'BayLaunchpad';

export default BayLaunchpad;

